// Local `$` reference parser/resolver for function expansion stage.
// Supports `$name`, `$name[index]`, and `$name.key` substitutions.
import { tokenizeLoopExpression } from '../../loops/for-loop/placeholder-expression-tokenize'
import { readVarCallToken } from '../../loops/for-loop/placeholder-expression-var'
import { resolveObjectKeyValue } from '../../variables/class-variable/transform/keys'
import type { LuisFunctionLocalScope } from '../types'
import { parseExpressionLocalReferenceAt, parseLocalReferenceAt } from './parse-reference'
import { rewriteExpressionLocalReferences } from './expression-references'

// Build one function-body source segment with resolved local `$` references.
export function replaceLocalReferences(source: string, scope: LuisFunctionLocalScope): string {
  let output = ''
  // Scan source and resolve `$name` tokens from function-local scope.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    if (char !== '$') {
      output += char
      continue
    } else {
      // Continue local token parsing for `$` candidates.
    }

    const reference = parseLocalReferenceAt(source, index)
    if (!reference) {
      const expression = rewriteLocalExpressionReference(source, index, scope)
      if (expression) {
        output += expression.value
        index = expression.end - 1
        continue
      }
      output += char
      continue
    } else {
      // Continue local scope lookup for parsed token.
    }

    const resolved = resolveLocalReference(scope, reference.name, reference.objectKey, reference.arrayIndex)
    if (typeof resolved === 'string') {
      // Keep deferred loop placeholders braced to prevent selector suffix merge.
      if (reference.raw.startsWith('${') && isDeferredLoopPlaceholder(resolved)) {
        output += `\${${resolved.slice(1)}}`
      } else {
        // Continue with direct local replacement.
        output += resolved
      }
    } else {
      output += reference.raw
    }
    index = reference.end - 1
  }

  // Return source with local `$` references resolved.
  return output
}

// Rewrite one braced local expression like `${i * j}` into concrete values.
function rewriteLocalExpressionReference(
  source: string,
  startIndex: number,
  scope: LuisFunctionLocalScope,
): { value: string; end: number } | null {
  const closeIndex = source.indexOf('}', startIndex + 2)
  if (source[startIndex + 1] !== '{' || closeIndex === -1) return null
  const expression = source.slice(startIndex + 2, closeIndex).trim()
  const rewritten = rewriteExpressionLocalReferences(expression, (reference) =>
    resolveExpressionIdentifier(scope, reference.raw, true))
  const tokens = tokenizeLoopExpression(rewritten.value)
  if (!tokens) return null
  const allowVarCallOperand = tokens.length !== 1 || tokens[0]?.kind !== 'identifier'
  let changed = rewritten.changed
  const value = tokens.map((token) => {
    if (token.kind === 'string') return `'${token.value.replace(/'/g, "\\'")}'`
    if (token.kind !== 'identifier') return token.value
    const resolved = resolveExpressionIdentifier(scope, token.value, allowVarCallOperand)
    if (resolved === null) return token.value
    changed = true
    return resolved
  }).join(' ')
  return changed ? { value: `\${${value}}`, end: closeIndex + 1 } : null
}

// Check whether the value is a deferred `$loop.key` placeholder.
function isDeferredLoopPlaceholder(value: string): boolean {
  // Regex matches deferred placeholders. Example match: "$bp.name"
  return /^\$[a-zA-Z_][\w-]*(?:\.[a-zA-Z_][\w-]*)*$/.test(value.trim())
}

// Resolve one expression identifier against local scope and format it safely.
function resolveExpressionIdentifier(
  scope: LuisFunctionLocalScope,
  identifier: string,
  allowVarCallOperand: boolean,
): string | null {
  const parsed = parseExpressionLocalReferenceAt(identifier, 0)
  if (!parsed || parsed.end !== identifier.length) return null
  const resolved = resolveLocalReference(scope, parsed.name, parsed.objectKey, parsed.arrayIndex)
  if (typeof resolved !== 'string') return null
  const deferredExpression = unwrapDeferredLoopExpression(resolved)
  if (deferredExpression) return deferredExpression
  const supportedVarCall = allowVarCallOperand ? normalizeSupportedVarCall(resolved) : null
  if (supportedVarCall) return supportedVarCall
  return isExpressionLiteralValue(resolved) ? resolved : `'${resolved.replace(/'/g, "\\'")}'`
}

// Unwrap deferred loop placeholder values so nested `@fun` math stays symbolic.
function unwrapDeferredLoopExpression(value: string): string | null {
  const trimmed = value.trim()
  if (isDeferredLoopPlaceholder(trimmed)) return `(${trimmed.slice(1)})`
  if (!trimmed.startsWith('${') || !trimmed.endsWith('}')) return null
  const inner = trimmed.slice(2, -1).trim()
  const tokens = inner ? tokenizeLoopExpression(inner) : null
  if (!tokens || tokens.length === 0) return null
  return `(${tokens.map((token) => token.value).join(' ')})`
}

// Check whether a local value can be injected into `${...}` without quoting.
function isExpressionLiteralValue(value: string): boolean {
  // Regex matches numeric literals with optional units. Example match: "12px"
  return /^-?\d+(?:\.\d+)?(?:[a-zA-Z%]+)?$/.test(value.trim())
}

// Normalize supported compile-time `var(...)` values for composite expressions.
function normalizeSupportedVarCall(value: string): string | null {
  const trimmed = value.trim()
  const parsed = readVarCallToken(trimmed, 0)
  return parsed && parsed.end === trimmed.length ? parsed.value : null
}

// Resolve one parsed local reference against scope maps.
function resolveLocalReference(
  scope: LuisFunctionLocalScope,
  name: string,
  objectKey: string | null,
  arrayIndex: number | null,
): string | null {
  if (objectKey) {
    const objectMap = scope.objects[name]
    const objectValue = objectMap ? resolveObjectKeyValue(objectMap, objectKey) : undefined
    // Return the resolved object-member value when it exists.
    return typeof objectValue === 'string' ? objectValue : null
  } else if (arrayIndex !== null) {
    const arrayValue = scope.arrays[name]?.[arrayIndex]
    // Return the resolved array-member value when it exists.
    return typeof arrayValue === 'string' ? arrayValue : null
  } else {
    // Return plain local value for direct `$name` references.
    return typeof scope.values[name] === 'string' ? scope.values[name] : null
  }
}
